import { appApiEndpoint } from "@/environment";
import { baseApi } from "./baseApi";

// Content Pages API slice - extends the base API
export const contentPagesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get paginated content pages
    getContentPages: builder.query({
      query: ({
        employerPermalink,
        perPage = 50,
        cursor = null,
        searchQuery = null,
        types = [],
        isPrefetchQuery = false,
      }) => {
        let url = new URL(
          `${appApiEndpoint}/v1/employers/${employerPermalink}/content-pages`
        );
        let params = new URLSearchParams();
        params.append("perPage", perPage);

        if (cursor) {
          params.append("cursor", cursor);
        }
        if (searchQuery) {
          params.append("search", searchQuery);
        }
        if (types && types.length > 0) {
          types.forEach((pageType) => {
            params.append("types[]", pageType);
          });
        }

        url.search = params.toString();
        return { url: url.toString(), method: "GET", isPrefetchQuery };
      },
      providesTags: ["ContentPages"],
    }),

    // Get single content page
    getContentPage: builder.query({
      query: ({ employerPermalink, contentPagePermalink }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/content-pages/${contentPagePermalink}`,
        method: "GET",
      }),
      providesTags: (result, error, { contentPagePermalink }) => [
        { type: "ContentPages", id: contentPagePermalink },
      ],
    }),

    // Create content page
    createContentPage: builder.mutation({
      query: ({ employerPermalink, contentPageData }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/content-pages`,
        method: "POST",
        data: contentPageData,
      }),
      invalidatesTags: ["ContentPages"],
    }),

    // Update content page
    updateContentPage: builder.mutation({
      query: ({
        employerPermalink,
        contentPagePermalink,
        contentPageData,
      }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/content-pages/${contentPagePermalink}`,
        method: "PUT",
        data: contentPageData,
      }),
      invalidatesTags: (result, error, { contentPagePermalink }) => [
        { type: "ContentPages", id: contentPagePermalink },
        "ContentPages",
      ],
    }),

    // Delete content page
    deleteContentPage: builder.mutation({
      query: ({ employerPermalink, contentPagePermalink }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/content-pages/${contentPagePermalink}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contentPagePermalink }) => [
        { type: "ContentPages", id: contentPagePermalink },
        "ContentPages",
      ],
    }),

    // Lock content page
    lockContentPage: builder.mutation({
      query: ({ employerPermalink, contentPagePermalink, payload }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/content-pages/${contentPagePermalink}/lock`,
        method: "PUT",
        data: payload,
      }),
      // No cache invalidation needed - lock is temporary and doesn't change content
    }),

    // Release content page
    releaseContentPage: builder.mutation({
      query: ({ employerPermalink, contentPagePermalink, payload }) => ({
        url: `${appApiEndpoint}/v1/employers/${employerPermalink}/content-pages/${contentPagePermalink}/release`,
        method: "PUT",
        data: payload,
      }),
      // No cache invalidation needed - release is temporary and doesn't change content
    }),

    // Get public content page
    getPublicContentPage: builder.query({
      query: ({ contentPagePermalink }) => ({
        url: `${appApiEndpoint}/v1/public/content-pages/${contentPagePermalink}`,
        method: "GET",
        withCredentials: false, // Public endpoint doesn't require credentials
      }),
      providesTags: (result, error, { contentPagePermalink }) => [
        { type: "PublicContentPage", id: contentPagePermalink },
      ],
    }),

    // Get public sales data
    getPublicSalesData: builder.query({
      query: ({ employerPermalink }) => ({
        url: `${appApiEndpoint}/v1/public/sales/${employerPermalink}`,
        method: "GET",
        withCredentials: false, // Public endpoint doesn't require credentials
      }),
      providesTags: (result, error, { employerPermalink }) => [
        { type: "PublicSalesData", id: employerPermalink },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetContentPagesQuery,
  useGetContentPageQuery,
  useCreateContentPageMutation,
  useUpdateContentPageMutation,
  useDeleteContentPageMutation,
  useLockContentPageMutation,
  useReleaseContentPageMutation,
  useGetPublicContentPageQuery,
  useGetPublicSalesDataQuery,
} = contentPagesApi;
